// ============================================================
// ByOurConsent.com -- Articles Page
// Matches approved mockup
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import useWindowWidth from '../hooks/useWindowWidth';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import api    from '../utils/api';

// ============================================================
// Sub-components
// ============================================================

const FeaturedArticle = ({ article }) => {
    const [hovered, setHovered] = useState(false);
    if (!article) return null;
    return (
        <Link to={`/articles/${article.slug}`} style={{ textDecoration: 'none' }}>
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    background:   '#f5f0e8',
                    border:       `0.5px solid ${hovered ? '#8b1a1a' : '#c8beaa'}`,
                    borderRadius: '4px',
                    padding:      '1.25rem',
                    marginBottom: '1.5rem',
                    cursor:       'pointer',
                    transition:   'border-color 0.15s'
                }}>
                <div style={{
                    fontSize: '9px', fontFamily: 'sans-serif',
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    background: '#8b1a1a', color: '#f5f0e8',
                    display: 'inline-block', padding: '2px 8px',
                    borderRadius: '2px', marginBottom: '10px'
                }}>
                    Featured
                </div>
                <h2 style={{
                    fontSize: '22px', fontWeight: 600, color: '#1e2a3a',
                    fontFamily: "'EB Garamond', Georgia, serif",
                    lineHeight: 1.2, marginBottom: '8px'
                }}>
                    {article.title}
                </h2>
                <p style={{
                    fontSize: '13px', fontFamily: 'sans-serif',
                    color: '#5a6070', lineHeight: 1.6, marginBottom: '12px'
                }}>
                    {article.excerpt}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                        width: '26px', height: '26px', borderRadius: '50%',
                        background: '#1e2a3a', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: '11px',
                        fontFamily: 'sans-serif', color: '#e8dfc8', fontWeight: 600
                    }}>
                        {(article.author_name || 'ED')[0]}
                    </div>
                    <span style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', fontWeight: 600 }}>
                        {(!article.author_name || article.author_name === 'Editorial Desk') ? 'Editorial Desk' : article.author_name}
                    </span>
                    <span style={{ color: '#c8beaa', fontSize: '10px' }}>·</span>
                    <span style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                        {article.display_date}
                    </span>
                    {article.reading_time_minutes && (
                        <>
                            <span style={{ color: '#c8beaa', fontSize: '10px' }}>·</span>
                            <span style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                {article.reading_time_minutes} min read
                            </span>
                        </>
                    )}
                </div>
            </div>
        </Link>
    );
};

const ArticleRow = ({ article }) => {
    const [hovered, setHovered] = useState(false);
    const width    = useWindowWidth();
    const isMobile = width < 768;
    return (
        <Link to={`/articles/${article.slug}`} style={{ textDecoration: 'none' }}>
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 90px',
                    gap: '14px', padding: '1.1rem 0',
                    borderBottom: '0.5px solid #c8beaa', cursor: 'pointer'
                }}>
                <div>
                    {/* Author name first -- clickable */}
                    {(article.author_name || article.author_display_name) && (
                        article.author_name === 'Editorial Desk' || !article.author_name ? (
                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8b6a3a', fontWeight: 600, marginBottom: '3px', letterSpacing: '0.04em' }}>
                                Editorial Desk{article.category_name && <span style={{ color: '#8a8070', fontWeight: 400 }}> · {article.category_name}</span>}
                            </div>
                        ) : (
                            <Link to={`/author/${encodeURIComponent(article.author_name)}`}
                                onClick={e => e.stopPropagation()}
                                style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8b6a3a', fontWeight: 600, marginBottom: '3px', letterSpacing: '0.04em', display: 'block', textDecoration: 'none' }}>
                                {article.author_name}
                                {article.category_name && <span style={{ color: '#8a8070', fontWeight: 400 }}> · {article.category_name}</span>}
                            </Link>
                        )
                    )}
                    {article.tags?.[0] && (
                        <div style={{
                            fontSize: '10px', fontFamily: 'sans-serif',
                            letterSpacing: '0.12em', textTransform: 'uppercase',
                            color: '#8b6a3a', marginBottom: '4px'
                        }}>
                            {article.tags[0]}
                        </div>
                    )}
                    <div style={{
                        fontSize: '16px', fontWeight: 600,
                        color: hovered ? '#8b1a1a' : '#1e2a3a',
                        fontFamily: "'EB Garamond', Georgia, serif",
                        lineHeight: 1.25, marginBottom: '5px',
                        transition: 'color 0.15s'
                    }}>
                        {article.title}
                    </div>
                    {article.excerpt && (
                        <div style={{
                            fontSize: '12px', fontFamily: 'sans-serif',
                            color: '#5a6070', lineHeight: 1.5, marginBottom: '6px'
                        }}>
                            {article.excerpt}
                        </div>
                    )}
                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                        {article.display_date}
                        {article.reading_time_minutes && ` · ${article.reading_time_minutes} min read`}
                    </div>
                </div>
                {article.cover_image_url ? (
                    <img src={article.cover_image_url} alt={article.title}
                         style={{ width: '90px', height: '68px', objectFit: 'cover',
                                  borderRadius: '3px', alignSelf: 'start', marginTop: '4px' }} />
                ) : null}
            </div>
        </Link>
    );
};

const SidebarCard = ({ title, children }) => (
    <div style={{
        background: '#f5f0e8', border: '0.5px solid #c8beaa',
        borderRadius: '4px', padding: '1rem', marginBottom: '1.1rem'
    }}>
        <div style={{
            fontSize: '12px', fontWeight: 600, color: '#1e2a3a',
            letterSpacing: '0.04em', marginBottom: '10px',
            paddingBottom: '8px', borderBottom: '0.5px solid #c8beaa'
        }}>
            {title}
        </div>
        {children}
    </div>
);

// ============================================================
// ArticlesPage
// ============================================================
const ArticlesPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const width    = useWindowWidth();
    const isMobile = width < 768;
    const [articles,    setArticles]    = useState([]);
    const [tags,        setTags]        = useState([]);
    const [categories,  setCategories]  = useState([]);
    const [authors,     setAuthors]     = useState([]);
    const [mostRead,    setMostRead]    = useState([]);
    const [pagination,  setPagination]  = useState(null);
    const [loading,     setLoading]     = useState(true);
    const [search,      setSearch]      = useState('');

    const activeTag      = searchParams.get('tag')      || '';
    const activeCategory = searchParams.get('category') || '';
    const activeAuthor   = searchParams.get('author')   || '';
    const activeSortBy   = searchParams.get('sort')     || 'date';
    const activePage     = parseInt(searchParams.get('page') || '1');

    const loadArticles = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: activePage, limit: 8 });
            if (activeTag)      params.set('tag', activeTag);
            if (activeCategory) params.set('category', activeCategory);
            if (activeAuthor)   params.set('author', activeAuthor);
            if (search.trim())  params.set('search', search.trim());
            if (activeSortBy === 'author') params.set('sort', 'author');

            const res = await api.get(`/articles?${params}`);
            setArticles(res.data.articles || []);
            setPagination(res.data.pagination);
        } catch {
            setArticles([]);
        } finally { setLoading(false); }
    }, [activePage, activeTag, activeCategory, activeAuthor, activeSortBy, search]);

    useEffect(() => { loadArticles(); }, [loadArticles]);

    useEffect(() => {
        api.get('/articles/tags')
            .then(res => setTags(res.data.tags || []))
            .catch(() => setTags(SAMPLE_TAGS));
        api.get('/articles/most-read?limit=5&days=30')
            .then(res => setMostRead(res.data.articles || []))
            .catch(() => {});
        api.get('/articles/categories')
            .then(res => setCategories(res.data.categories || []))
            .catch(() => {});
        api.get('/articles/authors')
            .then(res => setAuthors(res.data.authors || []))
            .catch(() => {});
    }, []);

    const featured  = articles[0] || null;
    const rest      = articles.slice(1);

    const setTag = (tag) => {
        const p = new URLSearchParams(searchParams);
        if (tag) p.set('tag', tag); else p.delete('tag');
        p.delete('page');
        setSearchParams(p);
    };

    const setCategory = (slug) => {
        const p = new URLSearchParams(searchParams);
        if (slug) p.set('category', slug); else p.delete('category');
        p.delete('page');
        setSearchParams(p);
    };

    const setSortBy = (sort) => {
        const p = new URLSearchParams(searchParams);
        if (sort && sort !== 'date') p.set('sort', sort); else p.delete('sort');
        p.delete('page');
        setSearchParams(p);
    };

    const setAuthor = (author) => {
        const p = new URLSearchParams(searchParams);
        if (author) p.set('author', author); else p.delete('author');
        p.delete('page');
        setSearchParams(p);
    };

    const setPage = (page) => {
        const p = new URLSearchParams(searchParams);
        p.set('page', page);
        setSearchParams(p);
        window.scrollTo(0, 0);
    };

    return (
        <>
            <Helmet>
                <title>Articles & Commentary — By Our Consent</title>
                <meta name="description" content="Constitutional governance, civic accountability, and rule of law — non-partisan analysis and commentary." />
            </Helmet>

            <Nav />

            {/* Page header */}
            <div style={{ background: '#1e2a3a', padding: '1.6rem 1.5rem' }}>
                <div style={{ maxWidth: '960px', margin: '0 auto', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                        <div style={{ fontSize: '10px', fontFamily: 'sans-serif', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '4px' }}>
                            From the desk
                        </div>
                        <h1 style={{ fontSize: '32px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '2px' }}>
                            Articles & commentary
                        </h1>
                        <div style={{ fontSize: '14px', color: '#6a7a8a', fontStyle: 'italic', fontFamily: "'EB Garamond', Georgia, serif" }}>
                            Analysis, history, and civic argument — no party line
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter toolbar */}
            <div style={{ background: '#ede8dc', borderBottom: '0.5px solid #c8beaa', padding: isMobile ? '10px 1rem' : '10px 1.5rem' }}>
                <div style={{ maxWidth: '960px', margin: '0 auto' }}>

                    {/* Category row */}
                    {categories.length > 0 && (
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', marginBottom:'8px' }}>
                            <span style={{ fontSize:'10px', fontFamily:'sans-serif', color:'#8a8070', letterSpacing:'0.1em', textTransform:'uppercase' }}>Category</span>
                            <button onClick={() => setCategory('')}
                                style={{ background: !activeCategory ? '#8b1a1a' : 'transparent', color: !activeCategory ? '#f5f0e8' : '#4a5060', border:`0.5px solid ${!activeCategory ? '#8b1a1a' : '#c8beaa'}`, padding:'3px 10px', fontSize:'11px', fontFamily:'sans-serif', borderRadius:'20px', cursor:'pointer' }}>
                                All
                            </button>
                            {categories.map(c => (
                                <button key={c.slug} onClick={() => setCategory(activeCategory === c.slug ? '' : c.slug)}
                                    style={{ background: activeCategory===c.slug ? '#8b1a1a' : 'transparent', color: activeCategory===c.slug ? '#f5f0e8' : '#4a5060', border:`0.5px solid ${activeCategory===c.slug ? '#8b1a1a' : '#c8beaa'}`, padding:'3px 10px', fontSize:'11px', fontFamily:'sans-serif', borderRadius:'20px', cursor:'pointer' }}>
                                    {c.name}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Author filter row */}
                    {authors.length > 0 && (
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px', flexWrap:'wrap' }}>
                            <span style={{ fontSize:'10px', fontFamily:'sans-serif', color:'#8a8070', letterSpacing:'0.1em', textTransform:'uppercase' }}>Author</span>
                            <button onClick={() => setAuthor('')}
                                style={{ background: !activeAuthor ? '#8b1a1a' : 'transparent', color: !activeAuthor ? '#f5f0e8' : '#4a5060', border:`0.5px solid ${!activeAuthor ? '#8b1a1a' : '#c8beaa'}`, padding:'3px 10px', fontSize:'11px', fontFamily:'sans-serif', borderRadius:'20px', cursor:'pointer' }}>
                                All
                            </button>
                            {authors.map(a => (
                                <button key={a.name} onClick={() => setAuthor(activeAuthor === a.name ? '' : a.name)}
                                    style={{ background: activeAuthor===a.name ? '#8b1a1a' : 'transparent', color: activeAuthor===a.name ? '#f5f0e8' : '#4a5060', border:`0.5px solid ${activeAuthor===a.name ? '#8b1a1a' : '#c8beaa'}`, padding:'3px 10px', fontSize:'11px', fontFamily:'sans-serif', borderRadius:'20px', cursor:'pointer' }}>
                                    {a.name}
                                </button>
                            ))}
                            <div style={{ marginLeft:'auto', position:'relative' }}>
                                <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
                                     style={{ position:'absolute', left:'9px', top:'50%', transform:'translateY(-50%)', opacity:0.4 }}>
                                    <circle cx="6.5" cy="6.5" r="4.5" stroke="#4a5060" strokeWidth="1.2"/>
                                    <line x1="10" y1="10" x2="14" y2="14" stroke="#4a5060" strokeWidth="1.2" strokeLinecap="round"/>
                                </svg>
                                <input type="text" value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && loadArticles()}
                                    placeholder="Search articles…"
                                    style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'6px 12px 6px 28px', fontSize:'12px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', width:'180px', outline:'none' }}
                                />
                            </div>
                        </div>
                    )}

                </div>{/* end filter toolbar inner */}
            </div>{/* end filter toolbar */}

            {/* Main content */}
            <div style={{ maxWidth: '960px', margin: '0 auto', padding: isMobile ? '1rem' : '1.5rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 260px', gap: '2rem' }}>

                {/* Articles column */}
                <div>
                    {loading ? (
                        <div style={{ padding: '3rem', textAlign: 'center', fontFamily: 'sans-serif', color: '#8a8070', fontSize: '14px' }}>
                            Loading articles...
                        </div>
                    ) : articles.length === 0 ? (
                        <div style={{ padding: '3rem', textAlign: 'center', fontFamily: 'sans-serif', color: '#8a8070', fontSize: '14px' }}>
                            No articles found.
                        </div>
                    ) : (
                        <>
                            {/* Featured first article */}
                            <FeaturedArticle article={featured} />

                            {/* Divider */}
                            {rest.length > 0 && (
                                <div style={{ height: '0.5px', background: '#c8beaa', marginBottom: '1.25rem' }} />
                            )}

                            {/* Rest of articles */}
                            <div>
                                {rest.map(a => (
                                    <ArticleRow key={a.id} article={a} />
                                ))}
                            </div>

                            {/* Pagination */}
                            {pagination && pagination.total_pages > 1 && (
                                <div style={{ display: 'flex', gap: '8px', marginTop: '1.5rem', alignItems: 'center' }}>
                                    {activePage > 1 && (
                                        <button onClick={() => setPage(activePage - 1)}
                                            style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '6px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '3px' }}>
                                            ← Previous
                                        </button>
                                    )}
                                    <span style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                        Page {activePage} of {pagination.total_pages}
                                    </span>
                                    {activePage < pagination.total_pages && (
                                        <button onClick={() => setPage(activePage + 1)}
                                            style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '6px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '3px' }}>
                                            Next →
                                        </button>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Sidebar */}
                <div>
                    {/* Most read */}
                    {mostRead.length > 0 && (
                        <SidebarCard title="Most read">
                            {mostRead.map((a, i) => (
                                <Link key={a.id} to={`/articles/${a.slug}`}
                                    style={{ display:'flex', gap:'8px', alignItems:'flex-start', padding:'7px 0', borderBottom: i < mostRead.length - 1 ? '0.5px solid #e0d8cc' : 'none', textDecoration:'none' }}>
                                    <span style={{ fontSize:'16px', fontWeight:700, color:'#c8beaa', fontFamily:"'EB Garamond', Georgia, serif", lineHeight:1, flexShrink:0, width:'18px' }}>
                                        {i + 1}
                                    </span>
                                    <div>
                                        <div style={{ fontSize:'12px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif", lineHeight:1.3, marginBottom:'2px' }}>
                                            {a.title}
                                        </div>
                                        {a.unique_view_count > 0 && (
                                            <div style={{ fontSize:'10px', fontFamily:'sans-serif', color:'#8a8070' }}>
                                                {a.unique_view_count.toLocaleString()} readers
                                            </div>
                                        )}
                                    </div>
                                </Link>
                            ))}
                        </SidebarCard>
                    )}

                </div>
            </div>

            <Footer />
        </>
    );
};

// ============================================================
// Sample data
// ============================================================
const SAMPLE_TAGS = [
    { tag: 'constitution' }, { tag: 'no kings' },
    { tag: 'anti-corruption' }, { tag: 'history' },
    { tag: 'civic action' }, { tag: 'rule of law' }
];

const PARTNER_ORGS = [
    { name: 'ACLU',             url: 'https://www.aclu.org' },
    { name: 'CCR Justice',      url: 'https://ccrjustice.org' },
    { name: 'Common Cause',     url: 'https://www.commoncause.org' },
    { name: 'Protect Democracy',url: 'https://protectdemocracy.org' },
    { name: 'Issue One',        url: 'https://issueone.org' }
];

export default ArticlesPage;
